import React, { useState, useMemo } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { Target, Crown, Download, Plus, ChevronRight } from 'lucide-react'
import { exportToCSV } from '@/lib/utils/export-utils'
import { ScenarioSelector } from './components/ScenarioSelector'
import { CreateScenarioModal } from './components/CreateScenarioModal'
import { DREBudgetModal } from './components/DREBudgetModal'
import { SetOfficialModal } from './components/SetOfficialModal'
import { useProaStore } from '@/stores/proa-store'
import { usePageTitle } from '@/stores/page-title-store'
import {
  useSelectedScenario,
  useSelectedScenarioComparisonWithRealData,
  useGetOfficialProa,
  enrichMetricsWithHierarchy,
} from './hooks/useProaData'
import type { ProaComparisonItem } from './hooks/useProaData'
import { SensitiveValue } from '@/components/privacy/SensitiveText'

function formatCurrency(value: number | null): string {
  if (value === null) return '—'
  const normalized = Object.is(value, -0) || value === 0 ? 0 : value
  return normalized.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
}

function formatPercent(value: number | null): string {
  if (value === null) return '—'
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`
}

function formatAchievement(value: number | null): string {
  if (value === null) return '—'
  return `${value.toFixed(1)}%`
}

function AchievementBar({ value, darkMode }: { value: number | null; darkMode?: boolean }) {
  if (value === null) return <span className="text-brand-gray-dark text-sm">—</span>
  const color = value >= 100 ? (darkMode ? 'bg-brand-dark-deepest' : 'bg-[#D2FA52]') : value >= 80 ? 'bg-yellow-400' : 'bg-red-400'
  const textColor = value >= 100 ? (darkMode ? 'text-brand-dark-deepest' : 'text-[#D2FA52]') : darkMode ? 'text-brand-dark-deepest' : value >= 80 ? 'text-yellow-400' : 'text-red-400'
  const clamped = Math.min(value, 120)
  const trackBg = darkMode ? 'bg-brand-dark-deepest/20' : 'bg-white/10'
  return (
    <div className="flex items-center gap-2">
      <span className={`text-sm font-body ${textColor}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatAchievement(value)}
      </span>
      <div className={`w-10 h-[3px] rounded-full ${trackBg} overflow-hidden`}>
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${(clamped / 120) * 100}%` }} />
      </div>
    </div>
  )
}

type PaceStatus = 'above-track' | 'on-track' | 'at-risk' | 'off-track'

const PACE_CONFIG: Record<PaceStatus, { label: string; color: string; bg: string; dot: string; border: string }> = {
  'above-track': { label: 'Acima do pace', color: 'text-brand-primary', bg: 'bg-brand-primary/10', dot: 'bg-brand-primary', border: 'border border-transparent' },
  'on-track': { label: 'No pace', color: 'text-brand-primary', bg: 'bg-transparent', dot: 'bg-brand-primary', border: 'border border-brand-primary' },
  'at-risk': { label: 'Em risco', color: 'text-yellow-400', bg: 'bg-yellow-400/10', dot: 'bg-yellow-400', border: 'border border-transparent' },
  'off-track': { label: 'Abaixo do pace', color: 'text-red-400', bg: 'bg-red-400/10', dot: 'bg-red-400', border: 'border border-transparent' },
}

function getPaceStatus(projecaoPct: number): PaceStatus {
  if (projecaoPct >= 105) return 'above-track'
  if (projecaoPct >= 97) return 'on-track'
  if (projecaoPct >= 90) return 'at-risk'
  return 'off-track'
}

function PaceBadge({ projecao, darkMode }: { projecao: number | null; darkMode?: boolean }) {
  if (projecao === null) return <span className="text-brand-gray-dark text-[10px]">—</span>
  const status = getPaceStatus(projecao)
  const cfg = PACE_CONFIG[status]
  const dotColor = darkMode ? 'bg-brand-dark-deepest' : cfg.dot
  const textColor = darkMode ? 'text-brand-dark-deepest' : cfg.color
  const bgColor = darkMode ? 'bg-brand-dark-deepest/10' : cfg.bg
  const borderColor = darkMode ? 'border border-brand-dark-deepest/30' : cfg.border
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md ${bgColor} ${borderColor}`}>
      <span className="relative inline-flex w-1.5 h-1.5">
        <span className={`absolute inline-flex h-full w-full rounded-full ${dotColor} opacity-75 animate-ping`} />
        <span className={`relative inline-flex w-1.5 h-1.5 rounded-full ${dotColor}`} />
      </span>
      <span className={`text-[10px] font-semibold ${textColor} whitespace-nowrap`}>
        {cfg.label}
      </span>
    </span>
  )
}

// Fixed-width value cell for aligned columns
function ValCell({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`w-[100px] text-left text-sm font-body shrink-0 ${className}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
      {children}
    </div>
  )
}

// Column header labels (dynamic based on selected year)
function getColumnHeaders(year: number) {
  const shortYear = year % 100
  const prevShortYear = (year - 1) % 100
  return [
    { key: 'proa', label: 'PROA', width: 'w-[140px]' },
    { key: 'proa_prev', label: 'PROA Ant.', width: 'w-[100px]' },
    { key: 'var_pct', label: `Var. ${shortYear}/${prevShortYear}`, width: 'w-[100px]' },
    { key: 'ytd', label: 'YTD', width: 'w-[100px]' },
    { key: 'ating', label: 'Atingimento', width: 'w-[120px]' },
    { key: 'pace', label: 'Pace', width: 'w-[110px]' },
    { key: 'proj', label: 'Projeção', width: 'w-[100px]' },
  ]
}

export default function ProaPage() {
  const { user } = useAuth()
  usePageTitle('PROA')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showDREBudgetModal, setShowDREBudgetModal] = useState(false)
  const [showSetOfficialModal, setShowSetOfficialModal] = useState(false)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())

  const { selectedYear } = useProaStore()
  const { data: selectedScenario } = useSelectedScenario()

  // Current scenario comparison (PROA + Real YTD)
  const {
    data: comparison,
    isLoading: isLoadingComparison,
  } = useSelectedScenarioComparisonWithRealData()

  // Previous year's official PROA
  const {
    data: previousProa,
    isLoading: isLoadingPrevious,
  } = useGetOfficialProa(selectedYear - 1)

  const isLoading = isLoadingComparison || isLoadingPrevious

  // Build enriched rows from comparison metrics
  const enrichedRows = useMemo(() => {
    if (!comparison?.metrics) return []
    return enrichMetricsWithHierarchy(comparison.metrics)
  }, [comparison])

  // Previous year PROA map
  const prevMap = useMemo(() => {
    const map = new Map<string, number>()
    if (previousProa?.metrics) {
      previousProa.metrics.forEach(m => map.set(m.lineItem, m.annual))
    }
    return map
  }, [previousProa])

  // Months elapsed for proportional YTD calculation
  const now = new Date()
  const monthsElapsed = selectedYear === now.getFullYear() ? now.getMonth() + 1 : 12

  const toggleSection = (id: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // CSV export
  const handleExportCSV = () => {
    if (!enrichedRows.length) return
    const exportData = enrichedRows.filter(r => !r.isGrouper).map(row => {
      const prev = prevMap.get(row.id) ?? null
      const varAbs = prev !== null ? row.proaAnnual - prev : null
      const varPct = prev !== null && prev !== 0 ? ((row.proaAnnual - prev) / Math.abs(prev)) * 100 : null
      const proaYTD = row.proaAnnual * (monthsElapsed / 12)
      const ating = row.realYTD !== null && proaYTD !== 0 ? (row.realYTD / proaYTD) * 100 : null
      return {
        'Linha DRE': row.name,
        [`PROA ${selectedYear}`]: row.proaAnnual,
        'YTD Real': row.realYTD ?? '',
        [`PROA ${selectedYear - 1}`]: prev ?? '',
        'Var. Absoluta': varAbs ?? '',
        'Var. %': varPct !== null ? `${varPct.toFixed(1)}%` : '',
        'Atingimento': ating !== null ? `${ating.toFixed(1)}%` : '',
      }
    })
    exportToCSV(exportData, `proa-${selectedScenario?.name || 'metas'}-${selectedYear}`)
  }

  // Admin-only access control
  if (!user || (user.role !== 'admin' && user.role !== 'diretor-executivo' && user.role !== 'diretor-financeiro' && user.role !== 'vice-presidente' && user.role !== 'ceo')) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6">
        <Target className="h-16 w-16 text-brand-red" />
        <p className="text-3xl font-bold text-white font-display">Acesso Restrito</p>
        <p className="text-brand-gray-light text-center max-w-md font-body text-lg">
          Esta p&aacute;gina cont&eacute;m informa&ccedil;&otilde;es de planejamento or&ccedil;ament&aacute;rio (PROA) e est&aacute; dispon&iacute;vel apenas para administradores.
        </p>
      </div>
    )
  }

  // Helper: compute row values
  const getRowValues = (item: ProaComparisonItem) => {
    const proaAtual = item.proaAnnual
    const proaAnterior = prevMap.get(item.id) ?? null
    const ytd = item.realYTD
    const varAbsoluta = proaAnterior !== null ? proaAtual - proaAnterior : null
    const varPercent = proaAnterior !== null && proaAnterior !== 0
      ? ((proaAtual - proaAnterior) / Math.abs(proaAnterior)) * 100
      : null
    const atingimento = ytd !== null && proaAtual !== 0
      ? (ytd / proaAtual) * 100
      : null
    const pace = ytd !== null && monthsElapsed > 0
      ? (ytd / monthsElapsed) * 12
      : null
    const projecao = pace !== null && proaAtual !== 0
      ? (pace / proaAtual) * 100
      : null
    return { proaAtual, proaAnterior, ytd, varAbsoluta, varPercent, atingimento, pace, projecao }
  }

  // Value metrics row (right side of every card)
  const renderMetrics = (vals: ReturnType<typeof getRowValues>, opts?: { bold?: boolean; muted?: boolean; greenProa?: boolean; darkText?: boolean }) => (
    <div className="flex items-center gap-6 shrink-0">
      <ValCell className="w-[140px]">
        <span className={opts?.greenProa ? 'text-brand-primary font-semibold' : opts?.darkText ? 'text-[#D2FA52] font-bold text-base bg-brand-dark-deepest px-2 py-0.5 rounded' : opts?.muted ? 'text-brand-gray-dark' : vals.proaAtual === 0 ? 'text-brand-gray-dark' : `text-white ${opts?.bold ? 'font-bold' : ''}`}>
          <SensitiveValue mask="****">{formatCurrency(vals.proaAtual)}</SensitiveValue>
        </span>
      </ValCell>
      <ValCell>
        <span className={opts?.darkText ? 'text-brand-dark-deepest font-bold' : 'text-brand-gray-dark'}>
          <SensitiveValue mask="****">{formatCurrency(vals.proaAnterior)}</SensitiveValue>
        </span>
      </ValCell>
      <ValCell>
        <span className={opts?.darkText ? 'text-brand-dark-deepest font-bold' : 'text-brand-gray-dark'}>
          {formatPercent(vals.varPercent)}
        </span>
      </ValCell>
      <ValCell>
        <span className={opts?.darkText ? 'text-brand-dark-deepest font-bold' : opts?.muted ? 'text-brand-gray-dark' : (vals.ytd === null || vals.ytd === 0) ? 'text-brand-gray-dark' : `text-white ${opts?.bold ? 'font-bold' : ''}`}>
          <SensitiveValue mask="****">{formatCurrency(vals.ytd)}</SensitiveValue>
        </span>
      </ValCell>
      <div className="w-[120px] shrink-0 flex justify-start">
        <AchievementBar value={vals.atingimento} darkMode={opts?.darkText} />
      </div>
      <div className="w-[110px] shrink-0 flex justify-start">
        <PaceBadge projecao={vals.projecao} darkMode={opts?.darkText} />
      </div>
      <ValCell>
        <span className={opts?.darkText ? 'text-brand-dark-deepest font-bold' : vals.projecao === null ? 'text-brand-gray-dark' : vals.projecao >= 100 ? 'text-[#D2FA52]' : vals.projecao >= 90 ? 'text-yellow-400' : 'text-red-400'}>
          {formatAchievement(vals.projecao)}
        </span>
      </ValCell>
    </div>
  )

  // Render cards from enriched data
  const renderCards = () => {
    const cards: React.ReactNode[] = []

    enrichedRows.forEach(item => {
      const isGrouper = item.isGrouper
      const isExpanded = expandedSections.has(item.id)
      const hasBreakdowns = item.breakdowns && item.breakdowns.length > 0
      const isBreakdownExpanded = expandedSections.has(`bd-${item.id}`)

      // Skip children of collapsed groupers
      if (item.parentId && !expandedSections.has(item.parentId)) return

      const vals = getRowValues(item)

      const isSubtotal = !isGrouper && item.parentId === null
      const isLucroLiquido = item.id === 'LucroLiquido'
      const isStretched = item.id === 'LucroLiquido120' || item.id === 'LucroLiquido150'

      const displayName = item.id === 'LucroLiquido' ? 'META 100%'
        : item.id === 'LucroLiquido120' ? 'Meta 120%'
        : item.id === 'LucroLiquido150' ? 'Meta 150%'
        : item.name

      if (isGrouper) {
        // Grouper card
        cards.push(
          <div
            key={item.id}
            className="rounded-lg border border-[#2A2D34] bg-brand-dark-light/60 cursor-pointer hover:border-brand-gray/40 transition-colors"
            onClick={() => toggleSection(item.id)}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center gap-2 min-w-0 mr-4">
                <ChevronRight
                  className={`h-4 w-4 text-brand-gray transition-transform duration-200 shrink-0 ${
                    isExpanded ? 'rotate-90' : ''
                  }`}
                />
                <span className="text-white font-body font-semibold text-sm uppercase tracking-wide truncate">
                  {item.name}
                </span>
              </div>
              {renderMetrics(vals, { greenProa: true })}
            </div>
          </div>
        )
      } else if (isLucroLiquido) {
        // META 100% card — green
        cards.push(
          <div key={item.id} className="rounded-lg bg-brand-primary">
            <div className="flex items-center justify-between px-5 py-4">
              <span className="font-body font-bold text-base tracking-wide text-brand-dark-deepest truncate min-w-0 mr-4">
                {displayName}
              </span>
              {renderMetrics(vals, { bold: true, darkText: true })}
            </div>
          </div>
        )
      } else if (isSubtotal) {
        // Subtotal card
        cards.push(
          <div
            key={item.id}
            className={`rounded-lg ${isStretched ? 'border border-[#D2FA52]/40 bg-brand-dark-light/30' : 'border border-[#2A2D34] border-l-2 border-l-brand-primary bg-[#2d3138]'}`}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <span className={`font-body truncate min-w-0 mr-4 ${isStretched ? 'text-brand-primary text-sm' : 'font-bold text-base tracking-wide text-white'}`}>
                {displayName}
              </span>
              {renderMetrics(vals, { bold: !isStretched, muted: isStretched })}
            </div>
          </div>
        )
      } else {
        // Regular child card
        cards.push(
          <div key={item.id} className="rounded-lg border border-[#2A2D34]/60 bg-brand-dark-light/30">
            <div
              className={`flex items-center justify-between px-5 py-3 ml-6 ${hasBreakdowns ? 'cursor-pointer hover:bg-brand-dark-light/60 transition-colors' : ''}`}
              onClick={hasBreakdowns ? () => toggleSection(`bd-${item.id}`) : undefined}
            >
              <div className="flex items-center gap-2 min-w-[180px] mr-4">
                {hasBreakdowns && (
                  <ChevronRight
                    className={`h-4 w-4 text-brand-gray transition-transform duration-200 ${
                      isBreakdownExpanded ? 'rotate-90' : ''
                    }`}
                  />
                )}
                <span className="text-brand-gray-light font-body text-sm">{item.name}</span>
              </div>
              {renderMetrics(vals)}
            </div>

            {/* Breakdown rows (client-level) */}
            {hasBreakdowns && isBreakdownExpanded && (
              <div className="border-t border-[#2A2D34]/40 mx-5 ml-12">
                {item.breakdowns!.map(bd => {
                  const bdAting = bd.realYTD !== null && bd.proaAnnual !== 0 ? (bd.realYTD / bd.proaAnnual) * 100 : null
                  const bdPace = bd.realYTD !== null && monthsElapsed > 0 ? (bd.realYTD / monthsElapsed) * 12 : null
                  const bdProjecao = bdPace !== null && bd.proaAnnual !== 0 ? (bdPace / bd.proaAnnual) * 100 : null

                  return (
                    <div key={`${item.id}-${bd.dimensionId}`} className="flex items-center justify-between py-2.5 border-b border-[#2A2D34]/20 last:border-b-0">
                      <span className="text-[#838998] font-body text-xs flex-1 min-w-0">{bd.dimensionLabel}</span>
                      <div className="flex items-center gap-6">
                        <ValCell className="w-[140px]"><span className="text-[#838998]"><SensitiveValue mask="****">{formatCurrency(bd.proaAnnual)}</SensitiveValue></span></ValCell>
                        <ValCell><span className="text-brand-gray-dark">{formatCurrency(null)}</span></ValCell>
                        <ValCell><span className="text-brand-gray-dark">{formatPercent(null)}</span></ValCell>
                        <ValCell><span className="text-[#838998]"><SensitiveValue mask="****">{formatCurrency(bd.realYTD)}</SensitiveValue></span></ValCell>
                        <div className="w-[120px] shrink-0 flex justify-start"><AchievementBar value={bdAting} /></div>
                        <div className="w-[110px] shrink-0 flex justify-start"><PaceBadge projecao={bdProjecao} /></div>
                        <ValCell>
                          <span className={bdProjecao === null ? 'text-brand-gray-dark' : bdProjecao >= 100 ? 'text-[#D2FA52]' : bdProjecao >= 90 ? 'text-yellow-400' : 'text-red-400'}>
                            {formatAchievement(bdProjecao)}
                          </span>
                        </ValCell>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      }
    })

    return cards
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        {/* Left: Title */}
        {selectedScenario ? (
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span className="text-brand-primary font-body font-light text-lg">{selectedYear}</span>
              {selectedScenario.isOfficial && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-brand-primary/15 border border-brand-primary/30 text-brand-primary text-[10px] font-semibold uppercase tracking-wide">
                  <Crown className="h-3 w-3" />
                  Cenário Oficial
                </span>
              )}
            </div>
            <span className="text-white font-body font-bold text-4xl">{selectedScenario.name}</span>
          </div>
        ) : (
          <div />
        )}

        {/* Right: Controls */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleExportCSV}
            disabled={!enrichedRows.length}
            className="px-3 py-2 text-sm font-medium bg-brand-dark-light text-white border border-gray-800/50 rounded-lg hover:border-gray-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4" />
            CSV
          </button>

          <ScenarioSelector onCreateNew={() => setShowCreateModal(true)} />

          <button
            onClick={() => setShowDREBudgetModal(true)}
            className="px-3 py-2 text-sm font-medium bg-brand-primary text-brand-dark-deepest rounded-lg hover:bg-brand-primary/90 transition-colors flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Editar Metas
          </button>

          {selectedScenario && !selectedScenario.isOfficial && (
            <button
              onClick={() => setShowSetOfficialModal(true)}
              className="px-3 py-2 text-sm font-medium bg-brand-dark-light text-brand-primary border border-brand-primary/30 rounded-lg hover:bg-brand-primary/10 transition-colors flex items-center gap-2"
            >
              <Crown className="h-4 w-4" />
              Definir Oficial
            </button>
          )}
        </div>
      </div>

      {/* No Scenario Selected */}
      {!selectedScenario && (
        <div className="bg-brand-dark-light/50 rounded-monumenta border border-gray-800/50 p-12">
          <div className="text-center space-y-4">
            <Target className="h-12 w-12 text-brand-gray mx-auto" />
            <h2 className="text-xl font-medium text-white font-body">Selecione um Cenário</h2>
            <p className="text-brand-gray max-w-md mx-auto font-body">
              Para visualizar e editar as metas orçamentárias, selecione um cenário no seletor acima ou crie um novo.
            </p>
          </div>
        </div>
      )}

      {/* Card List */}
      {selectedScenario && (
        <div>

          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary" />
            </div>
          )}

          {/* Column headers + Cards */}
          {!isLoading && (
            <div>
              {/* Column header row */}
              <div className="flex items-center justify-between px-5 py-2 mb-1">
                <div className="flex-1 min-w-0" />
                <div className="flex items-center gap-6">
                  {getColumnHeaders(selectedYear).map(col => (
                    <div key={col.key} className={`${col.width} shrink-0 text-left text-[10px] text-brand-gray uppercase tracking-wide font-body`}>
                      {col.label}
                    </div>
                  ))}
                </div>
              </div>
              {/* Cards */}
              <div className="space-y-2">
                {renderCards()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Spacer */}
      <div className="h-16" />

      {/* Modals */}
      <CreateScenarioModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />
      <DREBudgetModal
        isOpen={showDREBudgetModal}
        onClose={() => setShowDREBudgetModal(false)}
      />
      {selectedScenario && (
        <SetOfficialModal
          isOpen={showSetOfficialModal}
          onClose={() => setShowSetOfficialModal(false)}
          scenario={selectedScenario}
        />
      )}
    </div>
  )
}
